import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Title from "../common/Title";
import EnhancedGrid from "../common/EnhancedGrid";
import { MdSecurity } from "react-icons/md";
import Utils from "../../utils";
import { auditLogActions } from "../../actions/auditLog.actions";
import moment from "moment";
import AuditLogGridFilter from "./AuditLogGridFilter";
import GridHelper from "../helpers/gridHelper";
import { usePermissions } from "../../hooks/usePermissions";
import { Alert, Badge } from "react-bootstrap";
import { FaClipboardList } from "react-icons/fa";
import { getActionTypeLabel } from "../../constants/auditLog.constants";
import Joyride, { STATUS } from "react-joyride";
import { useTour } from "../../contexts/TourContext";

function AuditLogGrid(props) {
  const { hasPermission } = usePermissions(props.user);
  const location = useLocation();
  const dispatch = useDispatch();
  const auditLogReducer = useSelector((state) => state.auditLog);
  const [selectedAuditLog, setSelectedAuditLog] = useState(null);
  const { activeTour, endTour } = useTour();
  const [runTour, setRunTour] = useState(false);

  const filterInitialState = {
    email: "",
    actionType: [],
    affectedResourceType: "",
    affectedResourceId: "",
    isSuccessful: "",
    startDate: "",
    endDate: "",
    ipAddress: "",
    authenticationMethod: "",
    clientId: "",
  };
  const [filter, setFilter] = useState(filterInitialState);

  useEffect(() => {
    let queryParams = Utils.getQueryStringParams(location);

    // Respect the client selector dropdown:
    // - If user has a clientId selected (not empty), filter by that client
    // - If user selected "All Clients" (clientId is empty), don't filter
    // - SuperUsers can see "All Clients", others are restricted to their client
    if (props.user.clientId && props.user.clientGuid) {
      queryParams["clientId"] = props.user.clientId;
      queryParams["clientGuid"] = props.user.clientGuid;
    }

    dispatch(auditLogActions.get(queryParams));

    // Initialize filter from URL
    setFilter({
      email: queryParams["email"] || "",
      // Sent/read as a single comma-separated value (see FilterBar/objectToQueryString) —
      // split back into the array the multiselect filter works with.
      actionType: queryParams["actionType"] ? queryParams["actionType"].split(",") : [],
      affectedResourceType: queryParams["affectedResourceType"] || "",
      affectedResourceId: queryParams["affectedResourceId"] || "",
      isSuccessful: queryParams["isSuccessful"] || "",
      startDate: queryParams["startDate"] || "",
      endDate: queryParams["endDate"] || "",
      ipAddress: queryParams["ipAddress"] || "",
      authenticationMethod: queryParams["authenticationMethod"] || "",
      clientId: queryParams["clientId"] || "",
    });

    // eslint-disable-next-line
  }, [location.search, props.user.clientId]);

  useEffect(() => {
    if (activeTour === "auditlog") {
      setRunTour(true);
    }
  }, [activeTour]);

  const tourSteps = [
    {
      target: "body",
      content: "Welcome to the Audit Log! Let me show you how to track and monitor all system activities.",
      placement: "center",
      disableBeacon: true,
    },
    {
      target: ".audit-log-title",
      content: "The Audit Log tracks all user actions, security events, and system changes. This is essential for compliance and security monitoring.",
    },
    {
      target: '[data-tour="auditlog-filter"]',
      content: "Use these filters to search for specific events. You can filter by user, action type, date range, IP address, and more.",
    },
    {
      target: '[data-tour="auditlog-grid"]',
      content: "This table shows all audit log entries. Click on any row to see detailed information including old/new values and metadata.",
    },
    {
      target: '[data-tour="auditlog-grid"]',
      content: "Each entry shows when the action occurred, who performed it, what action was taken, which resource was affected, and whether it was successful.",
    },
  ];

  const handleJoyrideCallback = (data) => {
    const { status } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRunTour(false);
      endTour("auditlog");
    }
  };

  // Check if user has permission to view audit logs
  if (!props.user.isSuperUser && !props.user.isProductTeam && !props.user.isAdmin) {
    return (
      <>
        {!props.hideTitle && <Title text="Audit Log" icon={<MdSecurity className="mb-1" />} />}
        <Alert variant="danger">
          You do not have permission to view audit logs. This feature is only available to Super Users, Product Team, and Administrators.
        </Alert>
      </>
    );
  }

  const data = auditLogReducer.paginatedResult?.data.map((r) => {
    return {
      ...r,
      createdOn: r.createdOn
        ? moment(r.createdOn + 'Z').format("MM/DD/YYYY h:mm A")
        : "-",
      isSuccessful: r.isSuccessful ? (
        <Badge bg="success">Success</Badge>
      ) : (
        <Badge bg="danger">Failed</Badge>
      ),
      actionTypeDisplay: (
        <div>
          <div><strong>{getActionTypeLabel(r.actionType)}</strong></div>
          {r.actionDescription && (
            <small className="text-muted">{r.actionDescription}</small>
          )}
        </div>
      ),
      resource: r.affectedResourceType ? (
        <div>
          <div><strong>{r.affectedResourceType}</strong></div>
          {r.affectedResourceName && (
            <small className="text-muted">{r.affectedResourceName}</small>
          )}
          {!r.affectedResourceName && r.affectedResourceId && (
            <small className="text-muted">ID: {r.affectedResourceId}</small>
          )}
        </div>
      ) : "-",
    };
  });

  let displayColumns = [
    "createdOn",
    "email",
    "actionTypeDisplay",
    "resource",
    "isSuccessful",
    "ipAddress",
  ];

  return (
    <>
      <Joyride
        steps={tourSteps}
        run={runTour}
        continuous
        showProgress
        showSkipButton
        callback={handleJoyrideCallback}
        styles={{
          options: {
            primaryColor: "#cf7f00",
            textColor: "#334155",
            backgroundColor: "#ffffff",
            arrowColor: "#ffffff",
            zIndex: 10000,
          },
          buttonNext: {
            backgroundColor: "#cf7f00",
            borderRadius: "0.5rem",
            fontSize: "14px",
            padding: "10px 20px",
          },
          buttonBack: {
            color: "#cf7f00",
            marginRight: "10px",
          },
          buttonSkip: {
            color: "#64748b",
          },
        }}
      />
      {!props.hideTitle && (
        <div className="audit-log-title">
          <Title text="Audit Log" icon={<MdSecurity className="mb-1" />} />
        </div>
      )}

      <div data-tour="auditlog-filter">
        <AuditLogGridFilter filter={filter} user={props.user} />
      </div>

      <div data-tour="auditlog-grid">
        <EnhancedGrid
          gridId="auditlog"
          fillHeight={props.hideTitle}
          data={data}
          displayColumns={displayColumns}
          emptyState={{
            icon: <FaClipboardList size={80} className="text-muted mb-3" />,
            message: "No audit log entries",
            subtitle: "Activity will be logged here as users interact with the system.",
          }}
          idColumn={"auditLogId"}
          selectedRow={selectedAuditLog}
          onRowClick={(auditLog) => setSelectedAuditLog(auditLog)}
          pageNumber={(parseInt(Utils.getQueryStringParams(location).pageNumber) || 1) - 1}
          totalPages={auditLogReducer.paginatedResult?.totalPages ?? 0}
          loading={auditLogReducer.loading}
          onPageChange={GridHelper.handlePageClick}
          onSort={GridHelper.handleSortClick}
        />
      </div>

      {/* Detail Modal - will be implemented in AuditLogDetail */}
      {selectedAuditLog && (
        <div className="mt-3 p-3 bg-light rounded">
          <h5>Selected Audit Log</h5>
          <div className="row">
            <div className="col-md-6">
              <p><strong>Audit Log ID:</strong> {selectedAuditLog.auditLogId}</p>
              <p><strong>User:</strong> {selectedAuditLog.email}</p>
              <p><strong>Action:</strong> {getActionTypeLabel(selectedAuditLog.actionType)}</p>
              <p><strong>Description:</strong> {selectedAuditLog.actionDescription || "-"}</p>
            </div>
            <div className="col-md-6">
              <p><strong>Resource Type:</strong> {selectedAuditLog.affectedResourceType || "-"}</p>
              <p><strong>Resource ID:</strong> {selectedAuditLog.affectedResourceId || "-"}</p>
              <p><strong>IP Address:</strong> {selectedAuditLog.ipAddress || "-"}</p>
              <p><strong>Authentication Method:</strong> {selectedAuditLog.authenticationMethod || "-"}</p>
            </div>
          </div>
          {(selectedAuditLog.oldValue || selectedAuditLog.newValue) && (
            <div className="mt-3">
              <h6>Change Details</h6>
              <div className="row">
                <div className="col-md-6">
                  <p className="mb-1"><strong>Old Value:</strong></p>
                  <pre className="bg-white p-2 rounded" style={{ fontSize: "12px", maxHeight: "200px", overflow: "auto" }}>
                    {selectedAuditLog.oldValue || "-"}
                  </pre>
                </div>
                <div className="col-md-6">
                  <p className="mb-1"><strong>New Value:</strong></p>
                  <pre className="bg-white p-2 rounded" style={{ fontSize: "12px", maxHeight: "200px", overflow: "auto" }}>
                    {selectedAuditLog.newValue || "-"}
                  </pre>
                </div>
              </div>
            </div>
          )}
          {selectedAuditLog.metadata && (
            <div className="mt-2">
              <p><strong>Metadata:</strong></p>
              <pre className="bg-white p-2 rounded" style={{ fontSize: "12px" }}>
                {selectedAuditLog.metadata}
              </pre>
            </div>
          )}
          {selectedAuditLog.errorMessage && (
            <div className="mt-2">
              <Alert variant="danger">
                <strong>Error:</strong> {selectedAuditLog.errorMessage}
                {selectedAuditLog.errorCode && ` (Code: ${selectedAuditLog.errorCode})`}
              </Alert>
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default AuditLogGrid;
